import {
	IOperationResultNoValue,
	ITagAlert,
	ITagInteractionFilterOptions,
	ITagNotifyOptions,
	TagAlertViewModel,
	TagViewModel,
	UserReference,
	UserSessionContext,
} from '@ViewModels';
import { css } from 'aphrodite';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { destructive } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ISelectBoxOption, SelectBox } from '../../../SelectBox';
import { BulletIcon } from '../../../svgs/icons/BulletIcon';
import { TrashIcon } from '../../../svgs/icons/TrashIcon';
import { Tag } from '../../../tags/Tag';
import { styleSheet } from './styles';
import './styles.less';

type InteractionFilterSelectBoxOption = ISelectBoxOption<'account' | 'owner' | 'me' | 'user'> & {
	user?: UserReference;
};

const NotifyUserSelectBoxOptions: InteractionFilterSelectBoxOption[] = [
	{
		title: 'contact owner',
		value: 'owner',
	},
	{
		title: 'myself',
		value: 'me',
	},
];

const InteractionFilterSelectBoxOptions: InteractionFilterSelectBoxOption[] = [
	{
		title: 'anyone from the company',
		value: 'account',
	},
	{
		title: 'myself',
		value: 'me',
	},
	{
		title: 'contact owner',
		value: 'owner',
	},
];

export const EditTagAlertInteractionIntervalSelectBoxOptions: ISelectBoxOption<number>[] = [
	{
		title: 'week',
		value: 1 * 7,
	},
	{
		title: '2 weeks',
		value: 2 * 7,
	},
	{
		title: 'month',
		value: 1 * 30,
	},
	{
		title: '2 months',
		value: 2 * 30,
	},
	{
		title: '3 months',
		value: 3 * 30,
	},
	{
		title: '6 months',
		value: 6 * 30,
	},
	{
		title: 'year',
		value: 365,
	},
];

/** Utility method for creating ITagAlert with default options */
const createTagAlertWithDefaultOptions = (tagVm: TagViewModel) => {
	const tagAlert: ITagAlert = {
		interactionFilter: {
			accountWide: true,
			resourceOwner: false,
			user: null,
		},
		interactionIntervalInDays: 3 * 30,
		requireValidEmailAddress: true,
		sendAlertsTo: {
			resourceOwner: true,
			user: null,
		},
		tag: tagVm.value,
	};

	return tagAlert;
};

/**
 * Utility method for creating shallow copy
 *
 * @param tagAlertModel
 */
const createTagAlertModelShallowCopy = (tagAlertModel: ITagAlert) => {
	const shallowCopy: ITagAlert = {
		...tagAlertModel,
		interactionFilter: {
			user: null, // needed for mobx
			...tagAlertModel.interactionFilter,
		},
		requireValidEmailAddress: tagAlertModel.requireValidEmailAddress,
		sendAlertsTo: {
			user: null, // needed for mobx,
			...tagAlertModel.sendAlertsTo,
		},
	};

	return shallowCopy;
};

/** Utility method for converting ITagInteractionFilterOptions to InteractionFilterSelectBoxOption */
const tagInteractionFilterOptionsToSelectBoxOption = (
	userSession: UserSessionContext,
	filter: ITagInteractionFilterOptions,
	options = InteractionFilterSelectBoxOptions
) => {
	if (filter) {
		if (filter.accountWide) {
			return options.find(x => x.value === 'account');
		} else if (filter.user) {
			const value = userSession.user.id === filter.user.id ? 'me' : 'user';
			return options.find(x => x.value === value);
		} else {
			return options.find(x => x.value === 'owner');
		}
	}

	return options.find(x => x.value === 'account');
};

/** Utility method for converting ITagNotifyOptions to InteractionFilterSelectBoxOption */
const tagNotifyOptionsToSelectBoxOption = (
	userSession: UserSessionContext,
	notifyOptions: ITagNotifyOptions,
	selectBoxOptions = NotifyUserSelectBoxOptions
) => {
	if (!!notifyOptions && !!notifyOptions.user) {
		const value = userSession.user.id === notifyOptions.user.id ? 'me' : 'user';
		return selectBoxOptions.find(x => x.value === value);
	}

	return selectBoxOptions.find(x => x.value === 'owner');
};

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps, IErrorMessageComponentProps {
	className?: string;
	tagVm: TagViewModel;
	onClose?: (wasModified: boolean) => void;
}

interface IState {
	tagAlert?: TagAlertViewModel;
}

class _EditTagAlert extends React.Component<IProps, IState> {
	public readonly state: IState = {};
	private editableTagAlert: Partial<ITagAlert>;

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_EditTagAlert,
			| 'editableTagAlert'
			| 'interactionFilterSelectBoxOptions'
			| 'notifyUserSelectBoxOptions'
			| 'onAlertUserSelectBoxOptionChanged'
			| 'onInteractionFilterSelectBoxOptionChanged'
			| 'onInteractionIntervalSelectBoxOptionChanged'
			| 'onDeleteAlert'
		>(this, {
			editableTagAlert: observable,
			interactionFilterSelectBoxOptions: computed,
			notifyUserSelectBoxOptions: computed,
			onAlertUserSelectBoxOptionChanged: action,
			onInteractionFilterSelectBoxOptionChanged: action,
			onInteractionIntervalSelectBoxOptionChanged: action,
			onDeleteAlert: action,
		});
	}

	public UNSAFE_componentWillMount() {
		const nextState = this.getNextStateWithProps(this.props);
		if (nextState) {
			this.setState(nextState, this.loadTagAlert);
		}
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		const nextState = this.getNextStateWithProps(nextProps);
		if (nextState) {
			this.setState(nextState, this.loadTagAlert);
		}
	}

	public render() {
		const { tagAlert } = this.state;

		return (
			<div className={css(styleSheet.container)}>
				<div className='edit-tag-alert-content'>
					<span className={css(styleSheet.title)}>Keep in touch with contacts in this tag group</span>
					<div className={css(styleSheet.tag)}>
						<Tag tagValue={tagAlert.tagValue} />
					</div>
					<div className={css(styleSheet.description)}>
						Contacts in this group will appear as Suggested Keep in Touches, and Levitate will send notifications when
						communications decline.
					</div>
					{this.renderExpandedInfo()}
				</div>
				{!!tagAlert && !!tagAlert.isBusy && (
					<LoadingSpinner className='edit-tag-alert-loading absolute-center' type='large' />
				)}
			</div>
		);
	}

	/** Alert options and the save/cancel footer */
	private renderExpandedInfo() {
		const { userSession } = this.props;
		const { tagAlert } = this.state;

		const busy = !!tagAlert && !!tagAlert.isBusy;
		const disabled = !!busy || !this.editableTagAlert;
		const selectedNotifyOption =
			(!!this.editableTagAlert &&
				tagNotifyOptionsToSelectBoxOption(
					userSession,
					this.editableTagAlert.sendAlertsTo,
					this.notifyUserSelectBoxOptions
				)) ||
			null;
		const selectedInteractionFilterOption =
			(!!this.editableTagAlert &&
				tagInteractionFilterOptionsToSelectBoxOption(
					userSession,
					this.editableTagAlert.interactionFilter,
					this.interactionFilterSelectBoxOptions
				)) ||
			null;
		return (
			<div className={css(styleSheet.settings)}>
				<div className='edit-tag-alert-expanded-info-option'>
					<BulletIcon className='edit-tag-alert-expanded-info-option-icon' />
					<div className='edit-tag-alert-expanded-info-option-content'>
						<div>Notify if there&apos;s been no communication to this tag group from</div>
						<div className={css(styleSheet.verticalCenter)}>
							<SelectBox
								className='edit-tag-alert-interaction-filter-selector'
								disabled={disabled}
								onSelectionChanged={this.onInteractionFilterSelectBoxOptionChanged}
								options={this.interactionFilterSelectBoxOptions}
								selectedOption={selectedInteractionFilterOption || this.interactionFilterSelectBoxOptions[0]}
							/>
							<span>&nbsp; &nbsp; in the last &nbsp; &nbsp;</span>
							<SelectBox
								className='edit-tag-alert-interaction-interval-selector'
								disabled={disabled}
								onSelectionChanged={this.onInteractionIntervalSelectBoxOptionChanged}
								options={EditTagAlertInteractionIntervalSelectBoxOptions}
								selectedOption={EditTagAlertInteractionIntervalSelectBoxOptions.find(
									x => x.value === (this.editableTagAlert ? this.editableTagAlert.interactionIntervalInDays : 3 * 30)
								)}
							/>
						</div>
					</div>
				</div>
				<div className='edit-tag-alert-expanded-info-option'>
					<BulletIcon className='edit-tag-alert-expanded-info-option-icon' />
					<div className='edit-tag-alert-expanded-info-option-content'>
						<div>Send Suggested Keep in Touches & alert notifications to</div>
						<SelectBox
							className='edit-tag-alert-user-selector'
							disabled={disabled}
							onSelectionChanged={this.onAlertUserSelectBoxOptionChanged}
							options={this.notifyUserSelectBoxOptions}
							selectedOption={selectedNotifyOption || this.notifyUserSelectBoxOptions[0]}
						/>
					</div>
				</div>
				<div className='edit-tag-alert-expanded-info-option'>
					<div className='edit-tag-alert-expanded-info-option-content'>
						<Checkbox
							id='valid-emails-only'
							type='large'
							onChange={this.onValidEmailsOnlyChange}
							checked={this.editableTagAlert.requireValidEmailAddress}
							childrenPosition='right'
						>
							<span className={css(styleSheet.validEmailOnlyCheckbox)}>Only apply to contacts with emails</span>
						</Checkbox>
					</div>
				</div>
				<div className={css(styleSheet.footer)}>
					<div className={css(styleSheet.buttons)}>
						<button className={css(baseStyleSheet.ctaButton)} disabled={!!busy} onClick={this.onSaveClicked}>
							<span>Save</span>
						</button>
						<button className={css(baseStyleSheet.ctaButtonReverse)} disabled={!!busy} onClick={this.onCancelClicked}>
							<span>Cancel</span>
						</button>
					</div>
					{!!this.editableTagAlert && !!this.editableTagAlert.id && (
						<div onClick={this.onDeleteAlert} className={css(styleSheet.delete)}>
							<TrashIcon fillColor={destructive} />
							<span>Delete reminder</span>
						</div>
					)}
				</div>
			</div>
		);
	}

	private get interactionFilterSelectBoxOptions() {
		const { tagAlert } = this.state;
		const { userSession } = this.props;
		let options = [...InteractionFilterSelectBoxOptions];

		if (
			!!tagAlert &&
			!!tagAlert.isLoaded &&
			!!tagAlert.interactionFilter &&
			!!tagAlert.interactionFilter.user &&
			!!tagAlert.interactionFilter.user.id
		) {
			if (tagAlert.interactionFilter.user.id !== userSession.user.id) {
				options = [
					{
						title: getDisplayName(tagAlert.interactionFilter.user),
						user: tagAlert.interactionFilter.user,
						value: 'user',
					},
					...options,
				];
			}
		}

		return options;
	}

	private get notifyUserSelectBoxOptions() {
		const { tagAlert } = this.state;
		const { userSession } = this.props;

		let options = [...NotifyUserSelectBoxOptions];

		if (
			!!tagAlert &&
			!!tagAlert.isLoaded &&
			!!tagAlert.tagNotifyOptions &&
			!!tagAlert.tagNotifyOptions.user &&
			!!tagAlert.tagNotifyOptions.user.id
		) {
			if (tagAlert.tagNotifyOptions.user.id !== userSession.user.id) {
				options = [
					{
						title: getDisplayName(tagAlert.tagNotifyOptions.user),
						user: tagAlert.tagNotifyOptions.user,
						value: 'user',
					},
					...options,
				];
			}
		}

		return options;
	}

	private onAlertUserSelectBoxOptionChanged = (option: InteractionFilterSelectBoxOption) => {
		switch (option.value) {
			case 'owner': {
				this.editableTagAlert.sendAlertsTo.resourceOwner = true;
				this.editableTagAlert.sendAlertsTo.user = null;
				break;
			}
			default: {
				this.editableTagAlert.sendAlertsTo.resourceOwner = false;
				this.editableTagAlert.sendAlertsTo.user = {
					id: this.props.userSession.user.id,
				};
				break;
			}
		}
	};

	private onInteractionFilterSelectBoxOptionChanged = (option: InteractionFilterSelectBoxOption) => {
		switch (option.value) {
			case 'account': {
				this.editableTagAlert.interactionFilter.user = null;
				this.editableTagAlert.interactionFilter.resourceOwner = false;
				this.editableTagAlert.interactionFilter.accountWide = true;
				break;
			}
			case 'owner': {
				this.editableTagAlert.interactionFilter.user = null;
				this.editableTagAlert.interactionFilter.resourceOwner = true;
				this.editableTagAlert.interactionFilter.accountWide = false;
				break;
			}
			case 'user': {
				this.editableTagAlert.interactionFilter.user = option.user;
				this.editableTagAlert.interactionFilter.resourceOwner = false;
				this.editableTagAlert.interactionFilter.accountWide = false;
				break;
			}
			default: {
				this.editableTagAlert.interactionFilter.user = {
					id: this.props.userSession.user.id,
				};
				this.editableTagAlert.interactionFilter.resourceOwner = false;
				this.editableTagAlert.interactionFilter.accountWide = false;
				break;
			}
		}
	};

	private onInteractionIntervalSelectBoxOptionChanged = (option: ISelectBoxOption<number>) => {
		this.editableTagAlert.interactionIntervalInDays = option.value!;
	};

	private onCancelClicked = () => {
		const { tagVm } = this.props;
		const { tagAlert } = this.state;
		this.editableTagAlert = tagAlert.id
			? // FOLLOWUP: Resolve

				// @ts-ignore
				createTagAlertModelShallowCopy(tagAlert.toJs())
			: createTagAlertWithDefaultOptions(tagVm);

		this.closeDialog(false);
	};

	private closeDialog(wasModified: boolean) {
		const { onClose } = this.props;
		if (onClose) {
			onClose(wasModified);
		}
	}

	private onSaveClicked = () => {
		const { tagAlert } = this.state;
		const { logInput } = this.props;

		if (!!tagAlert && !tagAlert.isBusy) {
			if (this.editableTagAlert) {
				const promise = tagAlert.save(this.editableTagAlert) as Promise<ITagAlert> | null;
				if (promise) {
					const eventAction = `${this.editableTagAlert.id ? 'Edit' : 'Create'}TagAlert`;
					logInput(eventAction, 'Click', { ...this.editableTagAlert });
					promise
						.then(savedTagAlert => {
							this.editableTagAlert = createTagAlertModelShallowCopy(savedTagAlert);
							toast.success('Tag alert saved.');
							this.closeDialog(true);
						})
						.catch((error: IOperationResultNoValue) => {
							this.props.logEvent(`${eventAction}-Error`, { ...error });
						});
				}
			}
		}
	};

	private onDeleteAlert = () => {
		const { userSession, tagVm, logInput } = this.props;
		const { tagAlert } = this.state;

		if (!!this.editableTagAlert && !!this.editableTagAlert.id) {
			// delete
			const promise = tagAlert.delete();
			if (promise) {
				logInput('DeleteTagAlert', 'Click', { ...this.editableTagAlert });
				promise
					.then(() => {
						this.editableTagAlert = createTagAlertWithDefaultOptions(tagVm);
						this.setState({
							tagAlert: new TagAlertViewModel(userSession, {
								tag: tagVm.value,
							}),
						});
						toast.success('Tag alert removed.');
						this.closeDialog(true);
					})
					.catch((error: IOperationResultNoValue) => {
						this.props.logEvent('DeleteTagAlert-Error', { ...error });
					});
			}
		} else {
			this.closeDialog(false);
		}
	};

	private loadTagAlert = () => {
		const { tagAlert } = this.state;
		if (tagAlert) {
			const promise = tagAlert.load() as Promise<ITagAlert> | null;
			if (promise) {
				const { errorMessages, logEvent, tagVm } = this.props;
				promise
					.then(loadedTagAlert => {
						this.editableTagAlert = createTagAlertModelShallowCopy(loadedTagAlert);
					})
					.catch((error: IOperationResultNoValue) => {
						if (error.systemCode === 404) {
							this.editableTagAlert = createTagAlertWithDefaultOptions(tagVm);
						} else {
							logEvent('LoadTagAlert-Error', { ...error });
							errorMessages.push({
								messages: [error.systemMessage],
							});
						}
					});
			}
		}
	};

	private getNextStateWithProps(nextProps: IProps) {
		const nextState: IState = {};
		if (nextProps.tagVm !== this.props.tagVm || !this.state.tagAlert) {
			nextState.tagAlert = new TagAlertViewModel(nextProps.userSession, {
				tag: nextProps.tagVm.value,
			});

			this.editableTagAlert =
				!!nextState.tagAlert && !!nextState.tagAlert.id
					? // FOLLOWUP: Resolve

						// @ts-ignore
						createTagAlertModelShallowCopy(nextState.tagAlert.toJs())
					: createTagAlertWithDefaultOptions(nextProps.tagVm);
		}

		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	private onValidEmailsOnlyChange = () => {
		this.editableTagAlert.requireValidEmailAddress = !this.editableTagAlert.requireValidEmailAddress;
	};
}

const EditTagAlertAsObserver = observer(_EditTagAlert);
const EditTagAlertWithContext = inject(UserSessionViewModelKey, ErrorMessagesViewModelKey)(EditTagAlertAsObserver);
export const EditTagAlert = withEventLogging(EditTagAlertWithContext, 'EditTagAlert');
